import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import { Card, CardContent, Typography, Box } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import {
  chartColors,
  barDefaults,
  mergeOptions,
  getEfficiencyColor,
  withOpacity
} from '../../theme/chartTheme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function RefrigerantAnalysis({ data, title = 'Efficiency by Refrigerant Type', onBarClick }) {
  const chartData = useMemo(() => {
    // Group by refrigerant type
    const byRefrig = {}

    data.forEach(study => {
      if (!study.refrigerant || !study.efficiency_pct) return

      if (!byRefrig[study.refrigerant]) {
        byRefrig[study.refrigerant] = {
          effSum: 0,
          count: 0
        }
      }

      byRefrig[study.refrigerant].effSum += study.efficiency_pct
      byRefrig[study.refrigerant].count++
    })

    // Sort by average efficiency descending
    const sorted = Object.entries(byRefrig)
      .map(([refrig, data]) => ({
        refrig,
        avgEff: data.effSum / data.count,
        count: data.count
      }))
      // Show all refrigerant types
      .sort((a, b) => b.avgEff - a.avgEff)

    // Color each bar based on efficiency level
    const barColors = sorted.map(d => getEfficiencyColor(d.avgEff))

    return {
      labels: sorted.map(d => d.refrig),
      datasets: [
        {
          label: 'Average Efficiency Improvement (%)',
          data: sorted.map(d => d.avgEff),
          backgroundColor: barColors.map(c => withOpacity(c, 0.85)),
          hoverBackgroundColor: barColors,
          borderColor: barColors,
          borderWidth: 0,
          borderRadius: barDefaults.borderRadius
        }
      ]
    }
  }, [data])

  // Get sorted refrigerant keys for click handling
  const refrigKeys = useMemo(() => {
    const byRefrig = {}
    data.forEach(study => {
      if (!study.refrigerant || !study.efficiency_pct) return
      if (!byRefrig[study.refrigerant]) {
        byRefrig[study.refrigerant] = { effSum: 0, count: 0 }
      }
      byRefrig[study.refrigerant].effSum += study.efficiency_pct
      byRefrig[study.refrigerant].count++
    })
    return Object.entries(byRefrig)
      .map(([refrig, data]) => ({ refrig, avgEff: data.effSum / data.count, count: data.count }))
      .sort((a, b) => b.avgEff - a.avgEff)
      .map(d => d.refrig)
  }, [data])

  const options = mergeOptions({
    indexAxis: 'y',
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index
        const refrigerant = refrigKeys[index]
        onBarClick('refrigerant', refrigerant)
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (context) => `Refrigerant: ${context[0].label}`,
          label: (context) => {
            return `Efficiency: ${context.raw.toFixed(1)}%`
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Efficiency Improvement (%)',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          color: chartColors.grid
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      y: {
        title: {
          display: false
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11,
            weight: 500
          },
          color: chartColors.text
        }
      }
    }
  })

  // Count refrigerants shown in chart (those with 2+ data points)
  const refrigCount = useMemo(() => {
    return chartData.labels.length
  }, [chartData])

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: chartColors.textSecondary,
              backgroundColor: chartColors.grid,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 500
            }}
          >
            {refrigCount} types
          </Typography>
        </Box>
        <Box sx={{ height: 280 }}>
          <Bar data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default RefrigerantAnalysis
